import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@voilajsx/uikit/tabs';
import { Terminal, Package, Rocket } from 'lucide-react';

const QuickStart = () => {
  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">Quick Start</h1>
          <p className="text-lg text-muted-foreground">
            Get up and running with UIKit in under a minute.
          </p>
        </div>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Terminal className="w-6 h-6" />
            Option 1: CLI Scaffolding (Recommended)
          </h2>
          <p className="text-muted-foreground">
            The fastest way to start a new project with UIKit.
          </p>

          <Tabs defaultValue="fbca" className="w-full">
            <TabsList>
              <TabsTrigger value="fbca">FBCA</TabsTrigger>
              <TabsTrigger value="spa">SPA</TabsTrigger>
              <TabsTrigger value="multi">Multi</TabsTrigger>
              <TabsTrigger value="single">Single</TabsTrigger>
            </TabsList>

            <TabsContent value="fbca" className="mt-4">
              <CodePreview
                title="Feature-Based Component Architecture"
                code={`# Create a new FBCA project
npx @voilajsx/uikit create my-app --fbca

# Start development
cd my-app
npm run dev`}
                language="bash"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Best for: Large apps with multiple features. Includes auto-discovery routing.
              </p>
            </TabsContent>

            <TabsContent value="spa" className="mt-4">
              <CodePreview
                title="Single Page Application"
                code={`# Create a new SPA project
npx @voilajsx/uikit create my-app --spa

# Start development
cd my-app
npm run dev`}
                language="bash"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Best for: Medium apps with basic routing needs.
              </p>
            </TabsContent>

            <TabsContent value="multi" className="mt-4">
              <CodePreview
                title="Multi-Page Application"
                code={`# Create a new Multi-page project
npx @voilajsx/uikit create my-app --multi

# Start development
cd my-app
npm run dev`}
                language="bash"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Best for: Apps with complex, manually-defined routing.
              </p>
            </TabsContent>

            <TabsContent value="single" className="mt-4">
              <CodePreview
                title="Single Page"
                code={`# Create a new single page project
npx @voilajsx/uikit create my-app --single

# Start development
cd my-app
npm run dev`}
                language="bash"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Best for: Landing pages, simple sites without routing.
              </p>
            </TabsContent>
          </Tabs>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Package className="w-6 h-6" />
            Option 2: Add to Existing Project
          </h2>
          <p className="text-muted-foreground">
            Install UIKit in an existing React + Tailwind project.
          </p>

          <CodePreview
            title="Install Package"
            code={`npm install @voilajsx/uikit`}
            language="bash"
          />

          <CodePreview
            title="Setup ThemeProvider (main.tsx)"
            code={`import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider } from '@voilajsx/uikit/theme-provider';
import App from './App';
import './index.css';

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider defaultTheme="default" defaultMode="light">
      <App />
    </ThemeProvider>
  </React.StrictMode>
);`}
            language="tsx"
          />

          <CodePreview
            title="Import Styles (index.css)"
            code={`@import "tailwindcss";
@import "@voilajsx/uikit/styles";`}
            language="css"
          />
        </section>

        <Alert>
          <Rocket className="w-4 h-4" />
          <AlertDescription>
            <strong>Requirements:</strong> React 18+, Tailwind CSS 4+, Vite 7+ (for scaffolded projects)
          </AlertDescription>
        </Alert>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Your First Component</h2>
          <p className="text-muted-foreground">
            Once installed, you can import and use any component:
          </p>

          <CodePreview
            title="Using a Button"
            code={`import { Button } from '@voilajsx/uikit/button';

function App() {
  return (
    <div className="p-8">
      <Button variant="default">Default</Button>
      <Button variant="secondary">Secondary</Button>
      <Button variant="outline">Outline</Button>
      <Button variant="ghost">Ghost</Button>
      <Button variant="destructive">Destructive</Button>
    </div>
  );
}`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Project Scripts</h2>
          <p className="text-muted-foreground">
            All scaffolded projects include these npm scripts:
          </p>

          <div className="bg-muted/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-semibold">Script</th>
                  <th className="text-left p-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">npm run dev</td>
                  <td className="p-3 text-muted-foreground">Start development server</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">npm run build</td>
                  <td className="p-3 text-muted-foreground">Build for production</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">npm run preview</td>
                  <td className="p-3 text-muted-foreground">Preview production build</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">npm run deploy</td>
                  <td className="p-3 text-muted-foreground">Deploy with UIKit CLI</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">npm run prerender</td>
                  <td className="p-3 text-muted-foreground">Pre-render for SEO</td>
                </tr>
                <tr>
                  <td className="p-3 font-mono">npm run optimize</td>
                  <td className="p-3 text-muted-foreground">Optimize images</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Next Steps</h2>
          <p className="text-muted-foreground">
            Now that you have UIKit installed, continue to the next lesson to learn about the component system.
          </p>
        </section>
      </div>
    </TutorialLayout>
  );
};

export default QuickStart;
